import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw, GripVertical } from 'lucide-react';

const RULE_TYPES = [
  { value: 'exact_merchant_cardholder', label: 'Exact Merchant + Cardholder' },
  { value: 'exact_merchant_global',     label: 'Exact Merchant (Global)' },
  { value: 'keyword',                   label: 'Keyword Match' },
  { value: 'mcc',                       label: 'MCC Code' },
];

const PRIORITY_BADGE = {
  exact_merchant_cardholder: 'bg-slate-900 text-white',
  exact_merchant_global:     'bg-slate-700 text-white',
  keyword:                   'bg-blue-600 text-white',
  mcc:                       'bg-indigo-600 text-white',
};

function RuleDialog({ open, onClose, onSave, initial, categories, costCenters, cardholders }) {
  const [form, setForm] = useState({ name:'', rule_type:'exact_merchant_global', priority:50, merchant_normalized:'', cardholder_id:'', keyword:'', mcc_code:'', category_id:'', cost_center_id:'', description:'', is_active:true });
  useEffect(() => { if (open) setForm(initial ? {...initial} : { name:'', rule_type:'exact_merchant_global', priority:50, merchant_normalized:'', cardholder_id:'', keyword:'', mcc_code:'', category_id:'', cost_center_id:'', description:'', is_active:true }); }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle>{initial ? 'Edit' : 'New'} Allocation Rule</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 max-h-96 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e=>set('name',e.target.value)} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Priority (lower = higher)</Label><Input type="number" value={form.priority} onChange={e=>set('priority',parseInt(e.target.value)||50)} className="mt-1 h-8 text-sm" /></div>
          </div>
          <div>
            <Label className="text-xs">Rule Type *</Label>
            <Select value={form.rule_type} onValueChange={v => set('rule_type', v)}>
              <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{RULE_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {(form.rule_type === 'exact_merchant_cardholder' || form.rule_type === 'exact_merchant_global') && (
            <div><Label className="text-xs">Merchant (normalized fingerprint)</Label><Input value={form.merchant_normalized} onChange={e=>set('merchant_normalized',e.target.value)} className="mt-1 h-8 text-sm font-mono" placeholder="e.g. acmecloud, googlecloud" /></div>
          )}
          {form.rule_type === 'exact_merchant_cardholder' && (
            <div>
              <Label className="text-xs">Cardholder</Label>
              <Select value={form.cardholder_id} onValueChange={v => set('cardholder_id', v)}>
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{cardholders.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {form.rule_type === 'keyword' && (
            <div><Label className="text-xs">Keyword</Label><Input value={form.keyword} onChange={e=>set('keyword',e.target.value)} className="mt-1 h-8 text-sm" placeholder="e.g. Google Cloud" /></div>
          )}
          {form.rule_type === 'mcc' && (
            <div><Label className="text-xs">MCC Code</Label><Input value={form.mcc_code} onChange={e=>set('mcc_code',e.target.value)} className="mt-1 h-8 text-sm font-mono" /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category *</Label>
              <Select value={form.category_id} onValueChange={v => set('category_id', v)}>
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cost Center *</Label>
              <Select value={form.cost_center_id} onValueChange={v => set('cost_center_id', v)}>
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{costCenters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={e=>set('description',e.target.value)} className="mt-1 h-8 text-sm" /></div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={v => set('is_active', v)} />
            <Label className="text-xs">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => onSave(form)}>Save Rule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [cardholders, setCardholders] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, cc, ch] = await Promise.all([API.get('/rules'), API.get('/categories'), API.get('/cost-centers'), API.get('/cardholders')]);
      setRules(r.data); setCategories(c.data); setCostCenters(cc.data); setCardholders(ch.data);
    } catch(e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing) { await API.put(`/rules/${editing.id}`, form); toast.success('Updated'); }
      else { await API.post('/rules', form); toast.success('Created'); }
      setDialogOpen(false); setEditing(null); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const doDelete = async () => {
    try { await API.delete(`/rules/${deleteId}`); toast.success('Deleted'); setDeleteId(null); load(); }
    catch(e) { toast.error('Delete failed'); }
  };

  const RULE_TYPE_PRIORITY = {
    exact_merchant_cardholder: 1,
    exact_merchant_global: 2,
    keyword: 3,
    mcc: 4,
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Allocation Rules</h1>
          <p className="text-sm text-slate-500">Deterministic rules applied in priority order during import</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-rule-btn">
          <Plus size={14} className="mr-1" /> New Rule
        </Button>
      </div>

      {/* Priority legend */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
        <p className="text-xs font-semibold text-blue-700 mb-2">Rule Priority Order (deterministic)</p>
        <div className="flex flex-wrap gap-2">
          {['exact_merchant_cardholder','exact_merchant_global','keyword','mcc','recurring_pattern','prior_allocation','needs_review'].map((t, i) => (
            <span key={t} className={`text-xs px-2 py-0.5 rounded font-medium ${PRIORITY_BADGE[t] || 'bg-slate-100 text-slate-600'}`}>
              {i+1}. {t.replace(/_/g,' ')}
            </span>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="rules-table">
          <thead><tr>
            <th>Priority</th><th>Name</th><th>Type</th><th>Match</th>
            <th>Category</th><th>Cost Center</th><th>Status</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8"><RefreshCw size={16} className="animate-spin inline text-slate-400" /></td></tr>
            ) : rules.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-slate-400">No rules configured</td></tr>
            ) : rules.map(r => (
              <tr key={r.id}>
                <td className="tabular-nums font-mono text-sm">{r.priority}</td>
                <td className="font-medium">{r.name}</td>
                <td><span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${PRIORITY_BADGE[r.rule_type] || 'bg-slate-100'}`}>{r.rule_type?.replace(/_/g,' ')}</span></td>
                <td className="font-mono text-xs">{r.merchant_normalized || r.keyword || r.mcc_code || '—'}</td>
                <td className="text-xs">{r.category_name || r.category_id}</td>
                <td className="text-xs">{r.cost_center_name || r.cost_center_id}</td>
                <td><span className={`text-xs px-1.5 py-0.5 rounded border ${r.is_active ? 'chip-approved' : 'chip-rejected'}`}>{r.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="text-right">
                  <button onClick={() => { setEditing(r); setDialogOpen(true); }} className="p-1 text-slate-400 hover:text-emerald-600 mr-1"><Pencil size={13} /></button>
                  <button onClick={() => setDeleteId(r.id)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RuleDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }} onSave={save} initial={editing} categories={categories} costCenters={costCenters} cardholders={cardholders} />
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Delete Rule?</AlertDialogTitle><AlertDialogDescription>This rule will no longer apply to future imports.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
